/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../../../../logger';
import { debugPixel, generateUUID } from '../../../../util';
import { SiteConfig } from '../../../config';
import { BaseVideoPlacement, Quartile } from '../index';

import type { CampaignData, RenderedAdData } from '../../../../types/index';
import type { PrebidAdUnit } from '../../../../types/prebid';
import type { DigitekaWindow } from '../../../../types/vendor/digiteka';

const logger = Logger.getInstance();

class DigitekaVideoPlacement extends BaseVideoPlacement {
  private dtkWindow: DigitekaWindow;
  private playerReadyIntervalId: NodeJS.Timeout | null = null;
  private playerReadyCheckAttempts: number = 0;
  private readonly maxPlayerReadyCheckAttempts: number = 100; // 10 seconds total
  private readonly playerReadyCheckIntervalMs: number = 100;
  private listenersSetup: boolean = false;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig,
  ) {
    super(adUnit, adUnitTransformer, campaignData, siteConfig);
    this.dtkWindow = window as DigitekaWindow;
    try {
      debugPixel(
        {
          en: 'videoPlacementInitialised',
        },
        false,
      );
    } catch (error) {}
  }

  protected setSlot(): void {
    if (this.slot !== null) {
      return;
    }

    const slotElement = document.getElementById('player');
    if (!slotElement) {
      logger.warn(`No slot element found for divId 'player'`);
      return;
    }

    this.slot = slotElement;
  }

  protected setSupportedSizes(): void {}

  protected addListeners(): void {
    try {
      debugPixel(
        {
          en: 'addingVideoListeners',
        },
        false,
      );
    } catch (error) {}
    if (this.isPlayerReady()) {
      logger.debug('Digiteka player already ready, setting up IMA listeners immediately');
      this.setupImaListeners();
      try {
        debugPixel(
          {
            en: 'playerAlreadyReady',
          },
          false,
        );
      } catch (error) {}
    } else {
      logger.debug('Digiteka player not ready, combining event listener with polling fallback');
      try {
        debugPixel(
          {
            en: 'addingPlayerReadyEventListener',
          },
          false,
        );
      } catch (error) {}
      this.dtkWindow.addEventListener('dtkPlayerReady', () => {
        if (this.listenersSetup) {
          return;
        }
        logger.debug('Digiteka player ready event received');
        this.clearPlayerReadyInterval();
        this.setupImaListeners();
        try {
          debugPixel(
            {
              en: 'playerReadyEventReceived',
            },
            false,
          );
        } catch (error) {}
      });

      // Also poll as a fallback in case the event doesn't fire
      this.startPlayerReadyPolling();
    }
  }

  private startPlayerReadyPolling(): void {
    if (this.playerReadyIntervalId !== null) {
      return;
    }

    this.playerReadyCheckAttempts = 0;
    this.playerReadyIntervalId = setInterval(() => {
      this.playerReadyCheckAttempts++;

      if (this.isPlayerReady()) {
        logger.debug('Digiteka player ready detected via polling');
        this.clearPlayerReadyInterval();
        this.setupImaListeners();
        try {
          debugPixel(
            {
              en: 'playerReadyDetectedViaPolling',
            },
            false,
          );
        } catch (error) {}
        return;
      }

      if (this.playerReadyCheckAttempts >= this.maxPlayerReadyCheckAttempts) {
        this.clearPlayerReadyInterval();
        logger.warn(
          `Failed to detect Digiteka player ready after ${this.maxPlayerReadyCheckAttempts} attempts`,
        );
        try {
          debugPixel(
            {
              en: 'playerReadyTimeout',
              dtkImaAvailable: String(!!this.dtkWindow.dtkIma),
              googleImaAvailable: String(!!this.dtkWindow.google?.ima?.AdEvent?.Type),
            },
            false,
          );
        } catch (error) {}
      }
    }, this.playerReadyCheckIntervalMs);
  }

  private clearPlayerReadyInterval(): void {
    if (this.playerReadyIntervalId !== null) {
      clearInterval(this.playerReadyIntervalId);
      this.playerReadyIntervalId = null;
    }
  }

  protected addAdInteractionListener(): void {
    // Click listener is added in setupImaListeners
  }

  public destroy(): void {
    this.clearPlayerReadyInterval();
    super.destroy();
  }

  /**
   * Checks if Digiteka player and IMA SDK are already initialized and ready
   */
  private isPlayerReady(): boolean {
    return !!(this.dtkWindow.dtkIma && this.dtkWindow.google?.ima?.AdEvent?.Type);
  }

  private setupImaListeners(): void {
    if (this.listenersSetup) {
      return; // Prevent duplicate setup
    }

    if (!this.dtkWindow.dtkIma) {
      logger.warn('Digiteka IMA SDK is not available on the page');
      return;
    }

    if (!this.dtkWindow.google?.ima?.AdEvent?.Type) {
      logger.warn('Google IMA SDK is not available on the page');
      return;
    }

    try {
      debugPixel(
        {
          en: 'settingUpImaListeners',
        },
        false,
      );
    } catch (error) {}

    this.listenersSetup = true;

    const adEventType = this.dtkWindow.google.ima.AdEvent.Type;

    this.dtkWindow.dtkIma.addEventListener(adEventType.STARTED, (event: google.ima.AdEvent) =>
      this.adStartedHandler(event),
    );
    this.dtkWindow.dtkIma.addEventListener(adEventType.COMPLETE, (event: google.ima.AdEvent) =>
      this.adCompleteHandler(event),
    );
    this.dtkWindow.dtkIma.addEventListener(
      adEventType.FIRST_QUARTILE,
      (event: google.ima.AdEvent) => this.adFirstQuartileHandler(event),
    );
    this.dtkWindow.dtkIma.addEventListener(adEventType.MIDPOINT, (event: google.ima.AdEvent) =>
      this.adMidpointHandler(event),
    );
    this.dtkWindow.dtkIma.addEventListener(
      adEventType.THIRD_QUARTILE,
      (event: google.ima.AdEvent) => this.adThirdQuartileHandler(event),
    );
    this.dtkWindow.dtkIma.addEventListener(adEventType.PAUSED, () =>
      this.pauseViewabilityTracking(),
    );
    this.dtkWindow.dtkIma.addEventListener(adEventType.RESUMED, () =>
      this.resumeViewabilityTracking(),
    );
    this.dtkWindow.dtkIma.addEventListener(adEventType.SKIPPED, (event: google.ima.AdEvent) =>
      this.adSkippedHandler(event),
    );
    this.dtkWindow.dtkIma.addEventListener(adEventType.CLICK, (event: google.ima.AdEvent) =>
      this.adClickedHandler(event),
    );

    logger.debug('Digiteka IMA event listeners registered');
  }

  /**
   * Determines if an ad is a direct sold ad based on the business rules:
   *
   * AdSystem == 'GDFP' AND Wrapper Ad Systems is empty
   * OR
   * AdSystem != 'GDFP' AND Wrapper Ad Systems is NOT empty AND last Wrapper Ad Systems == 'GDFP' AND advertiser name is ''
   */
  private isDirectSoldAd(ad: google.ima.Ad): boolean {
    const adSystem = ad.getAdSystem();
    const wrapperAdSystems = ad.getWrapperAdSystems();
    const advertiserName = ad.getAdvertiserName();

    // Rule 1: AdSystem == 'GDFP' AND Wrapper Ad Systems is empty
    if (adSystem === 'GDFP' && wrapperAdSystems.length === 0) {
      return true;
    }

    // Rule 2: AdSystem != 'GDFP' AND Wrapper Ad Systems is NOT empty
    // AND last Wrapper Ad Systems == 'GDFP' AND advertiser name is ''
    if (
      adSystem !== 'GDFP' &&
      wrapperAdSystems.length > 0 &&
      wrapperAdSystems[wrapperAdSystems.length - 1] === 'GDFP' &&
      advertiserName === ''
    ) {
      return true;
    }

    return false;
  }

  private adStartedHandler(event: google.ima.AdEvent): void {
    if (this.adCurrentlyPlaying) {
      try {
        debugPixel(
          {
            en: 'videoAdStartedWhileAnotherPlaying',
          },
          false,
        );
      } catch (error) {}
      return;
    }

    this.adStarted();

    const ad = event.getAd();
    if (!ad) {
      logger.warn('Ad object is null in adStartedHandler event');
      return;
    }

    logger.debug(
      `Digiteka Ad Started event received - AdId: ${ad.getAdId()}, AdSystem: ${ad.getAdSystem()}, WrapperAdSystems: [${ad.getWrapperAdSystems().join(', ')}], AdvertiserName: ${ad.getAdvertiserName()}, CreativeId: ${ad.getCreativeId()}`,
    );

    if (!this.isDirectSoldAd(ad)) {
      logger.debug('Ad is not a direct sold ad, ignoring');
      try {
        debugPixel(
          {
            en: 'programmaticVideoAdStarted',
          },
          false,
        );
      } catch (error) {}
      // Exchange-bought: no ad-server-resolvable creative id, but the
      // clickthrough chain often identifies the buyer, and an advertiser
      // domain is all a programmatic capture needs.
      this.captureExchangeAdByClickThrough(
        ad.getTraffickingParametersString(),
        ad.getDuration(),
      );
      return;
    }

    try {
      debugPixel(
        {
          en: 'directVideoAdStarted',
        },
        false,
      );
    } catch (error) {}

    // Check if the current opportunity has already completed
    const currentOpportunity = this.currentOpportunity;
    if (currentOpportunity && this.completedOpportunityIds.has(currentOpportunity.id)) {
      return;
    }

    this.adCurrentlyPlaying = true;

    // Get the outermost wrapper ad ID if available
    const wrapperAdIds = ad.getWrapperAdIds();
    const lineItemId =
      wrapperAdIds.length > 0 ? wrapperAdIds[wrapperAdIds.length - 1] : ad.getAdId();

    // Get the outermost wrapper creative ID if available
    const wrapperCreativeIds = ad.getWrapperCreativeIds();
    const creativeId =
      wrapperCreativeIds && wrapperCreativeIds.length > 0
        ? wrapperCreativeIds[wrapperCreativeIds.length - 1]
        : ad.getCreativeId();

    const renderedAdData: RenderedAdData = {
      lineItemId: lineItemId || undefined,
      creativeId: creativeId || undefined,
      // Nameless direct ads may be pass-through creatives; a resolvable click
      // chain names the actual buyer next to the creative id (see
      // clickThroughAdvertiserDomains). Named ads are attributed by the ad
      // server's own advertiser and never overridden from the chain.
      advertiserDomains:
        ad.getAdvertiserName() === ''
          ? this.clickThroughAdvertiserDomains(ad.getTraffickingParametersString())
          : undefined,
      durationSeconds: ad.getDuration() ? Math.round(ad.getDuration()) : undefined,
      nodalsDealId: ad.getDealId() || undefined,
      isPrebidAd: false,
      isProgrammaticBuy: false,
      responseIdentifier: generateUUID(),
      size: { width: this.playerWidth, height: this.playerHeight },
    };

    this.handleRenderedAdData(renderedAdData);
  }

  private adCompleteHandler(_event: google.ima.AdEvent): void {
    const currentOpportunity = this.currentOpportunity;
    if (!currentOpportunity) {
      return;
    }

    // Check if this opportunity has already completed
    if (this.completedOpportunityIds.has(currentOpportunity.id)) {
      return;
    }

    logger.debug('Digiteka Ad Complete event received');

    this.dispatchAdQuartileEvent(Quartile.FOURTH);
    this.adCurrentlyPlaying = false;
    // Note: completedOpportunityIds is set inside dispatchAdQuartileEvent when FOURTH quartile is dispatched
  }

  private adFirstQuartileHandler(_event: google.ima.AdEvent): void {
    logger.debug('Digiteka Ad First Quartile event received');

    this.dispatchAdQuartileEvent(Quartile.FIRST);
  }

  private adMidpointHandler(_event: google.ima.AdEvent): void {
    logger.debug('Digiteka Ad Midpoint event received');

    this.dispatchAdQuartileEvent(Quartile.SECOND);
  }

  private adThirdQuartileHandler(_event: google.ima.AdEvent): void {
    logger.debug('Digiteka Ad Third Quartile event received');

    this.dispatchAdQuartileEvent(Quartile.THIRD);
  }

  private adSkippedHandler(_event: google.ima.AdEvent): void {
    logger.debug('Digiteka Ad Skipped event received');
    this.adCurrentlyPlaying = false;
  }

  private adClickedHandler(_event: google.ima.AdEvent): void {
    if (!this.adCurrentlyPlaying) {
      return;
    }

    logger.debug('Digiteka Ad Clicked event received');

    this.dispatchAdInteraction();
  }
}

export { DigitekaVideoPlacement };
